import Admin from 'admin';
import adminRouteConfig from 'admin/adminRouteConfig';
import { isSiteAdmin, showAdminOnLocalhost } from 'app/lib/utils/adaya/AdayaUtils';
import Account from 'components/pages/Account';
import Answers from 'components/pages/AnswersIframe';
import AcceptInvite from 'components/pages/auth/AcceptInvite';
import ForgotPassword from 'components/pages/auth/ForgotPassword';
import Login from 'components/pages/auth/Login';
import Logout from 'components/pages/auth/Logout';
import ResetPassword from 'components/pages/auth/ResetPassword';
import Signup from 'components/pages/auth/Signup';
import AmazonAdsAuthorize from 'components/pages/callbacks/amazon/AmazonAdsAuthorize';
import AmazonAdsCallback from 'components/pages/callbacks/amazon/AmazonAdsCallback';
import GoogleAdsAuthorize from 'components/pages/callbacks/google/GoogleAdsAuthorize';
import GoogleCallback from 'components/pages/callbacks/google/GoogleCallback';
import ShopifyAuthorize from 'components/pages/callbacks/shopify/ShopifyAuthorize';
import ShopifyCallback from 'components/pages/callbacks/shopify/ShopifyCallback';
import TikTokAuthorize from 'components/pages/callbacks/tiktok/TikTokAuthorize';
import TikTokCallback from 'components/pages/callbacks/tiktok/TikTokCallback';
import Campaigns from 'components/pages/Campaigns';
import Goal from 'components/pages/Goal';
import GoalMobile from 'components/pages/Goal/Mobile';
import Insights from 'components/pages/Insights';
import Mer from 'components/pages/Mer';
import NotFoundPage from 'components/pages/NotFound';
import StyleGuide from 'components/pages/StyleGuide';
import Track from 'components/pages/Track';
import TrackMobile from 'components/pages/Track/Mobile';
import AdsManagerOld from 'components/pages/Tracking/AdsManager';
import AdsManager from 'components/pages/Tracking/AdsManagerUpdate';
import DashboardV4 from 'app/demo/v4/DashboardV4'; // v4 redesigned Ads Manager
import OverviewV4 from 'app/demo/v4/OverviewV4';
import PredictiveV4 from 'app/demo/v4/PredictiveV4';
import MerV4 from 'app/demo/v4/MerV4';
import GoalTrackerV4 from 'app/demo/v4/GoalTrackerV4';
import GoalStudioV4 from 'app/demo/v4/GoalStudioV4';
import DailyVelocityV4 from 'app/demo/v4/DailyVelocityV4';
import TrendsV4 from 'app/demo/v4/TrendsV4';
import InsightsV4 from 'app/demo/v4/InsightsV4';
import TrackingEvents from 'components/pages/Tracking/TrackingEvents';
import Trends from 'components/pages/Trends';
import Loader from 'components/shared/app/Loader';
import MobileComponent from 'components/shared/app/MobileComponent';
import React from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import AdCreative from './pages/AdCreative';
import HUB from './pages/HUB';
import LPManager from './pages/Tracking/LPManager';
import UserJourneys from './pages/Tracking/UserJourneys';

const SecureRoute = (props) => {
    const location = useLocation();
    const redirectStr = location ? (`?redirect=${encodeURIComponent(props.path + (location?.search || ''))}`) : '';
    const loader = () => <Loader customText="Initializing user..." />;
    const login = () => <Navigate to={`/login${redirectStr}`} />;

    if (props.auth.authFinished) {
        if (props.auth.loggedIn && props.auth.loggedInProviders)
            return props.auth.userInitialized ? props.children : loader();
        else return login();
    }

    return loader();
};

const AdminRoute = props => {
    const location = useLocation();
    const redirectStr = location ? (`?redirect=${props.path + (location.search || '')}`) : '';
    return (props.auth.loggedIn && (isSiteAdmin(props.auth.user) || showAdminOnLocalhost())) ?
        props.children :
        (props.auth.loggedIn ?
            <Navigate to={`/not-found`} replace /> :
            <Navigate to={`/login${redirectStr}`} replace />
        );
};

export default (props) => {

    return (
        <Routes>
            {/* ------------ PUBLIC ROUTES ------------ */}

            <Route exact path="/" element={<Navigate replace to="/login" />} />

            <Route exact path="/signup" element={<Signup {...props} />} />

            <Route exact path="/login" element={<Login {...props} />} />

            <Route exact path="/accept-invite/:token" element={<AcceptInvite {...props} />} />

            <Route exact path="/forgot-password" element={<ForgotPassword {...props} />} />

            <Route exact path="/reset-password/:token" element={<ResetPassword {...props} />} />

            <Route exact path="/logout" element={<Logout {...props} />} />

            <Route exact path="/shopify-authorize" element={<ShopifyAuthorize {...props} />} />

            <Route exact path="/googleads-authorize" element={<GoogleAdsAuthorize {...props} />} />

            <Route exact path="/tiktok-authorize" element={<TikTokAuthorize {...props} />} />

            <Route exact path="/amazon_ads-authorize" element={<AmazonAdsAuthorize {...props} />} />

            <Route exact path="/answers" element={<Answers {...props} />} />

            {/* ------------ PRIVATE ROUTES ------------ */}

            <Route
                exact
                path="/shopify-callback"
                element={
                    <SecureRoute path="/shopify-callback" {...props}>
                        <ShopifyCallback {...props} />
                    </SecureRoute>
                }
            />

            <Route
                exact
                path="/googleads-callback"
                element={
                    <SecureRoute path="/googleads-callback" {...props}>
                        <GoogleCallback {...props} />
                    </SecureRoute>
                }
            />

            <Route
                exact
                path="/tiktok-callback"
                element={
                    <SecureRoute path="/tiktok-callback" {...props}>
                        <TikTokCallback {...props} />
                    </SecureRoute>
                }
            />

            <Route
                exact
                path="/amazon-callback"
                element={
                    <SecureRoute path="/amazon-callback" {...props}>
                        <AmazonAdsCallback {...props} />
                    </SecureRoute>
                }
            />

            <Route
                exact
                path="/overview"
                element={
                    <SecureRoute path="/overview" {...props}>
                        <OverviewV4 {...props} />
                    </SecureRoute>
                }
            />

            <Route
                exact
                path="/account"
                element={
                    <SecureRoute path="/account" {...props}>
                        <Account {...props} />
                    </SecureRoute>
                }
            />

            <Route
                exact
                path="/insights"
                element={
                    <SecureRoute path="/insights" {...props}>
                        <InsightsV4 {...props} />
                    </SecureRoute>
                }
            />

            <Route
                exact
                path="/track"
                element={
                    <SecureRoute path="/track" {...props}>
                        <DailyVelocityV4 {...props} />
                    </SecureRoute>
                }
            />

            <Route
                exact
                path="/trends"
                element={
                    <SecureRoute path="/trends" {...props}>
                        <TrendsV4 {...props} />
                    </SecureRoute>
                }
            />

            <Route
                exact
                path="/campaigns"
                element={
                    <SecureRoute path="/campaigns" {...props}>
                        <PredictiveV4 {...props} />
                    </SecureRoute>
                }
            />

            <Route
                exact
                path="/mer"
                element={
                    <SecureRoute path="/mer" {...props}>
                        <MerV4 {...props} />
                    </SecureRoute>
                }
            />

            <Route
                exact
                path="/goals"
                element={
                    <SecureRoute path="/goals" {...props}>
                        <GoalTrackerV4 {...props} />
                    </SecureRoute>
                }
            />

            <Route
                exact
                path="/goals/edit"
                element={
                    <SecureRoute path="/goals/edit" {...props}>
                        <GoalStudioV4 {...props} />
                    </SecureRoute>
                }
            />

            <Route
                exact
                path="/ads-manager"
                element={
                    <SecureRoute path="/ads-manager" {...props}>
                        <DashboardV4 {...props} />
                    </SecureRoute>
                }
            />

            <Route
                exact
                path="/ads-manager-old"
                element={
                    <SecureRoute path="/ads-manager-old" {...props}>
                        <AdsManagerOld {...props} />
                    </SecureRoute>
                }
            />

            <Route
                exact
                path="/lp-manager"
                element={
                    <SecureRoute path="/lp-manager" {...props}>
                        <LPManager {...props} />
                    </SecureRoute>
                }
            />

            <Route
                exact
                path="/tracking"
                element={
                    <SecureRoute path="/tracking" {...props}>
                        <TrackingEvents {...props} />
                    </SecureRoute>
                }
            />

            <Route
                exact
                path="/tracking/feed"
                element={
                    <SecureRoute path="/tracking/feed" {...props}>
                        <TrackingEvents {...props} />
                    </SecureRoute>
                }
            />

            <Route
                exact
                path="/user-journeys"
                element={
                    <SecureRoute path="/user-journeys" {...props}>
                        <UserJourneys {...props} />
                    </SecureRoute>
                }
            />

            {
                <Route
                    exact
                    path="/ad-creative"
                    element={
                        <AdminRoute path="/ad-creative" {...props}>
                            <AdCreative {...props} />
                        </AdminRoute>
                    }
                />
            }

            <Route
                exact
                path="/styles"
                element={
                    <SecureRoute path="/styles" {...props}>
                        <StyleGuide {...props} />
                    </SecureRoute>
                }
            />

            <Route
                exact
                path="/hub"
                element={
                    <SecureRoute path="/hub" {...props}>
                        <HUB {...props} />
                    </SecureRoute>
                }
            />

            {/* ADMIN ROUTES */}

            {Object.values(adminRouteConfig).map((Config) => {
                return (
                    <Route
                        exact
                        path={`${Config.path}`}
                        key={`${Config.path}`}
                        element={
                            <AdminRoute path={`${Config.path}`} {...props}>
                                <Admin {...props}>{Config.component && <Config.component {...props} />}</Admin>
                            </AdminRoute>
                        }
                    />
                );
            })}

            {/* CATCH ALL */}

            <Route path="/not-found" element={<NotFoundPage {...props} />} />

            <Route path="*" element={<Navigate to="/not-found" />} />
        </Routes>
    );
};
